import React, { useEffect, useRef, useState } from 'react'
import './saveConflict.css'

function RecoveryDialog({ onResolve, onClose }) {
  const dialogRef = useRef(null)
  const headingRef = useRef(null)
  const busyRef = useRef(false)
  const [choice, setChoice] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const dialog = dialogRef.current
    dialog.showModal()
    return () => dialog.close()
  }, [])

  useEffect(() => { headingRef.current?.focus() }, [choice])

  const confirm = async () => {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setError(null)
    try {
      await onResolve(choice)
      onClose()
    } catch (failure) {
      setError(failure.message || 'Could not resolve the save conflict. Your current arrangement is still open. Please try again.')
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  return (
    <dialog ref={dialogRef} className="save-conflict-dialog" aria-labelledby="save-conflict-title"
      onCancel={event => { event.preventDefault(); if (!busyRef.current) onClose() }}>
      <h2 ref={headingRef} tabIndex={-1} id="save-conflict-title">Resolve save conflict</h2>
      <p>This sheet has another saved version. Close other tabs editing this sheet before continuing.</p>
      {!choice ? (
        <>
          <p>Nothing will be replaced until you confirm your choice.</p>
          <div className="save-conflict-actions">
            <button type="button" onClick={() => setChoice('local')}>Keep this tab’s arrangement</button>
            <button type="button" onClick={() => setChoice('saved')}>Load saved version</button>
            <button type="button" onClick={onClose}>Keep editing for now</button>
          </div>
        </>
      ) : (
        <>
          <p>{choice === 'local'
            ? 'This will replace the saved version with this tab’s arrangement, sizes and quantities. Changes saved from another tab will be replaced.'
            : 'This will replace this tab’s arrangement with the saved version. Unsaved changes in this tab will be discarded.'}</p>
          {error && <p role="alert">{error}</p>}
          <div className="save-conflict-actions">
            <button type="button" disabled={busy} onClick={confirm}>
              {busy ? 'Resolving…' : choice === 'local' ? 'Confirm: save this tab’s version' : 'Confirm: load saved version'}
            </button>
            <button type="button" disabled={busy} onClick={() => { setChoice(null); setError(null) }}>Back</button>
            <button type="button" disabled={busy} onClick={onClose}>Cancel</button>
          </div>
        </>
      )}
    </dialog>
  )
}

export default function SaveConflictNotice({ onResolve }) {
  const [open, setOpen] = useState(false)
  return (
    <aside className="save-conflict-notice" aria-label="Save conflict">
      <span>Saving is paused because this sheet changed elsewhere. Keep this tab open until you choose a version.</span>
      <button type="button" onClick={() => setOpen(true)}>Resolve save conflict</button>
      {open && <RecoveryDialog onResolve={onResolve} onClose={() => setOpen(false)} />}
    </aside>
  )
}
